import React from "react";
import { Mail, Phone, Car, Store, UserRound, ShieldCheck, Star, CalendarDays, MapPin } from "lucide-react";
import { StatusBadge } from "../app/ui";
import { formatDate, titleCase } from "../../lib/api";

const roleIcon = { driver: Car, restaurant: Store, rider: UserRound, admin: ShieldCheck };

function Row({ icon: Icon, label, value, testId }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex items-center gap-2 text-sm" data-testid={testId}>
      <Icon className="h-4 w-4 shrink-0 text-ink-muted" />
      <span className="text-ink-muted">{label}:</span>
      <span className="font-medium text-ink">{value}</span>
    </div>
  );
}

/** Full profile + contact card for a user, used by the admin Users and Reports detail views. */
export default function ProfileBlock({ profile, heading }) {
  if (!profile) return <p className="text-sm text-ink-muted">No profile found.</p>;
  const Icon = roleIcon[profile.role] || UserRound;
  return (
    <div className="rounded-2xl border border-surface-200 bg-white p-4" data-testid={`profile-${profile.user_id}`}>
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-50 text-brand">
            <Icon className="h-5 w-5" />
          </div>
          <div>
            {heading && <p className="text-[10px] font-bold uppercase tracking-wider text-ink-muted">{heading}</p>}
            <p className="font-semibold text-ink">{profile.name || "—"}
              {profile.suspended && <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase text-red-700">Suspended</span>}
              {profile.upheld_reports >= 2 && <span className="ml-2 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-bold uppercase text-white" data-testid="repeat-offender-badge">⚠ {profile.upheld_reports} upheld reports</span>}
            </p>
            <p className="text-xs text-ink-soft">{profile.user_type_label}</p>
          </div>
        </div>
        {profile.is_partner !== false && profile.verification_status ? <StatusBadge status={profile.verification_status} /> : null}
      </div>
      <div className="grid gap-1.5">
        <Row icon={Mail} label="Email" value={profile.email} testId="profile-email" />
        <Row icon={Phone} label="Phone" value={profile.phone} testId="profile-phone" />
        {profile.rating ? <Row icon={Star} label="Rating" value={Number(profile.rating).toFixed(1)} /> : null}
        {profile.vehicle_category ? <Row icon={Car} label="Vehicle" value={titleCase((profile.vehicle_category || "").replace(/_/g, " "))} /> : null}
        {profile.vehicle_make || profile.vehicle_model ? <Row icon={Car} label="Car" value={`${profile.vehicle_make || ""} ${profile.vehicle_model || ""}`.trim()} /> : null}
        {profile.vehicle_plate ? <Row icon={Car} label="Plate" value={profile.vehicle_plate} /> : null}
        {profile.taxi_permit_number ? <Row icon={ShieldCheck} label="Permit" value={`${profile.taxi_permit_number} (${titleCase(profile.taxi_permit_status || "n/a")})`} /> : null}
        {profile.base_address ? <Row icon={MapPin} label="Base" value={profile.base_address} /> : null}
        {profile.restaurant ? <Row icon={Store} label="Restaurant" value={`${profile.restaurant.name}${profile.restaurant.is_open ? " · Open" : " · Closed"}`} /> : null}
        {profile.restaurant?.address ? <Row icon={MapPin} label="Address" value={profile.restaurant.address} /> : null}
        {profile.is_online !== null && profile.is_online !== undefined ? <Row icon={ShieldCheck} label="Status" value={profile.is_online ? "Online" : "Offline"} /> : null}
        <Row icon={CalendarDays} label="Joined" value={profile.created_at ? formatDate(profile.created_at, { timeStyle: undefined }) : null} />
        {profile.suspended && profile.suspended_reason ? <Row icon={ShieldCheck} label="Suspend reason" value={profile.suspended_reason} /> : null}
      </div>
      {(profile.rides_count != null || profile.orders_count != null || profile.rides_as_driver != null || profile.deliveries != null || profile.private_bookings != null) && (
        <div className="mt-3 flex flex-wrap gap-2 border-t border-surface-200 pt-3">
          {profile.rides_count != null && <Stat label="Rides" value={profile.rides_count} />}
          {profile.rides_as_driver != null && <Stat label="Rides driven" value={profile.rides_as_driver} />}
          {profile.deliveries != null && <Stat label="Deliveries" value={profile.deliveries} />}
          {profile.orders_count != null && <Stat label="Orders" value={profile.orders_count} />}
          {profile.private_bookings != null && <Stat label="Private bookings" value={profile.private_bookings} />}
        </div>
      )}
      {(profile.recent_rides?.length > 0 || profile.recent_orders?.length > 0) && (
        <div className="mt-3 space-y-2 border-t border-surface-200 pt-3" data-testid={`recent-activity-${profile.user_id}`}>
          {profile.recent_rides?.length > 0 && (
            <div>
              <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-ink-muted">Recent rides</p>
              {profile.recent_rides.map((r) => (
                <div key={r.ride_id} className="flex items-center justify-between gap-2 py-0.5 text-xs">
                  <span className="truncate text-ink-soft">{r.pickup_address} → {r.drop_address}</span>
                  <span className="shrink-0 text-ink-muted">{titleCase((r.status || "").replace(/_/g, " "))} · {r.created_at ? formatDate(r.created_at, { timeStyle: undefined }) : ""}</span>
                </div>
              ))}
            </div>
          )}
          {profile.recent_orders?.length > 0 && (
            <div>
              <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-ink-muted">Recent orders</p>
              {profile.recent_orders.map((o) => (
                <div key={o.order_id} className="flex items-center justify-between gap-2 py-0.5 text-xs">
                  <span className="truncate text-ink-soft">{o.restaurant_name}</span>
                  <span className="shrink-0 text-ink-muted">{titleCase((o.status || "").replace(/_/g, " "))} · {o.created_at ? formatDate(o.created_at, { timeStyle: undefined }) : ""}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl bg-surface-100 px-3 py-1.5 text-center">
      <p className="text-sm font-bold text-ink">{value}</p>
      <p className="text-[10px] uppercase tracking-wide text-ink-muted">{label}</p>
    </div>
  );
}
